import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircleIcon } from '@heroicons/react/20/solid'
import { Radio, RadioGroup } from '@headlessui/react'

import Modal from 'src/components/Modal'
import Image from 'src/components/Image'
import { useBackend } from 'src/BackendProvider'
import { useDesigns } from './DesignsProvider'
import { useDesignRegistration } from './DesignRegistration'
import { useRoles } from '../user/Auth'
import { useSingleTeamMangement } from '../Management'
import { design_figure } from 'src/data'
import { DesignFigure } from './designs'
import DeleteButton from '../components/DeleteButton'
import { IconEdit } from '../components/icons'

export default function FiguresTab() {
    const { t } = useTranslation()
    const { isEditUser } = useRoles()
    const { team } = useSingleTeamMangement()
    const { registration } = useDesignRegistration()
    const { figuresByRegistrationId, postFigure, deleteFigure, reloadFigures } = useDesigns()

    const [editingFigure, setEditingFigure] = useState<DesignFigure | null>(null)

    if (!registration) return null

    const registrationId = registration.registrationId
    const registrationFigures = figuresByRegistrationId[registrationId] || []

    const handleAddClick = async () => {
        // Create the figure immediately so we have a figureId for the Image component
        // Calculate next figure number
        const nextFigureNumber = registrationFigures.length > 0
            ? Math.max(...registrationFigures.map(f => f.figureNumber)) + 1
            : 1
        const newFigure: DesignFigure = {
            registrationId,
            realm: team?.realm ?? '',
            figureNumber: nextFigureNumber,
            description: undefined,
            showOnOverview: false
        }
        const createdFigure = await postFigure(newFigure)
        await reloadFigures()
        setEditingFigure(createdFigure)
    }

    const handleSaveFigure = async (figure: DesignFigure) => {
        await postFigure(figure)
        await reloadFigures()
        setEditingFigure(null)
    }

    const handleDeleteFigure = async (figureId: number) => {
        const figure = registrationFigures.find(f => f.figureId === figureId)
        if (figure) {
            await deleteFigure(figure)
            await reloadFigures()
        }
        setEditingFigure(null)
    }

    // Handle single figure selection (radio button behavior)
    const handleFigureSelection = async (figureId: number | null) => {
        // Deselect currently selected figure
        const currentlySelected = registrationFigures.find(f => f.showOnOverview)
        if (currentlySelected && currentlySelected.figureId !== figureId) {
            await postFigure({ ...currentlySelected, showOnOverview: false })
        }
        // Select the new figure (if not deselecting)
        if (figureId !== null) {
            const newSelected = registrationFigures.find(f => f.figureId === figureId)
            if (newSelected && !newSelected.showOnOverview) {
                await postFigure({ ...newSelected, showOnOverview: true })
            }
        }
        await reloadFigures()
    }

    // Get currently selected figure ID for RadioGroup
    const selectedFigureId = registrationFigures.find(f => f.showOnOverview)?.figureId ?? null

    return (
        <>
            <div className="flex flex-row justify-between items-start mb-2">
                <h3>{t('figures')}</h3>
                {isEditUser && (
                    <button onClick={handleAddClick} className="btn-secondary py-0.5 text-sm whitespace-nowrap">
                        {t('add')}
                    </button>
                )}
            </div>

            {registrationFigures.length === 0 ? (
                <div className="text-gray-500 py-4">
                    {t('no-figures')}
                </div>
            ) : (
                <RadioGroup
                    value={selectedFigureId}
                    onChange={handleFigureSelection}
                    className="flex flex-col gap-4"
                    disabled={!isEditUser}
                >
                    {[...registrationFigures].sort((a, b) => a.figureNumber - b.figureNumber).map((fig) => (
                        <Radio value={fig.figureId} key={fig.figureId}>
                            {({ checked }) => (
                                <FigureRow
                                    figure={fig}
                                    checked={checked}
                                    isEditUser={isEditUser}
                                    onEdit={() => setEditingFigure(fig)}
                                    onDelete={() => deleteFigure(fig)}
                                    onDescriptionChange={async (description) => {
                                        await postFigure({ ...fig, description: description || undefined })
                                        await reloadFigures()
                                    }}
                                />
                            )}
                        </Radio>
                    ))}
                </RadioGroup>
            )}

            {/* Add/Edit Figure Modal */}
            {editingFigure && (
                <EditFigureModal
                    figure={editingFigure}
                    onClose={() => setEditingFigure(null)}
                    onSave={handleSaveFigure}
                    onDelete={handleDeleteFigure}
                />
            )}
        </>
    )
}

function FigureRow({
    figure,
    checked,
    isEditUser,
    onEdit,
    onDelete,
    onDescriptionChange
}: {
    figure: DesignFigure
    checked: boolean
    isEditUser: boolean
    onEdit: () => void
    onDelete: () => void
    onDescriptionChange: (description: string) => Promise<void>
}) {
    const { t } = useTranslation()
    const { images } = useBackend()
    const [description, setDescription] = useState(figure.description || '')
    const [isUpdating, setIsUpdating] = useState(false)

    // Sync description state when figure prop changes
    useEffect(() => {
        setDescription(figure.description || '')
    }, [figure.description])

    const image = figure.imageId ? images.find(i => i.imageId === figure.imageId) : undefined

    const handleDescriptionBlur = async () => {
        if (description !== (figure.description || '')) {
            setIsUpdating(true)
            await onDescriptionChange(description)
            setIsUpdating(false)
        }
    }

    return (
        <div className="grid grid-cols-[40px_150px_1fr_60px] gap-4 items-start bg-pc-200 p-3 rounded-lg cursor-pointer">
            {/* Radio indicator */}
            <div className="flex items-start justify-center pt-2">
                {checked
                    ? <CheckCircleIcon className="w-6 h-6 text-pcx-600" />
                    : <div className="w-6 h-6"><div className="mx-0.5 my-0.5 w-5 h-5 border-2 border-gray-400 rounded-full" /></div>
                }
            </div>

            {/* Image */}
            <div className="flex flex-col items-center">
                {image ? (
                    <img
                        src={image.url}
                        alt={t('figure')}
                        className="w-32 h-32 object-contain"
                    />
                ) : (
                    <div className="w-32 h-32 bg-gray-100 rounded border flex items-center justify-center text-gray-400 text-xs">
                        {t('no-image')}
                    </div>
                )}
            </div>

            {/* Description textarea */}
            <div className="flex-1">
                <textarea
                    className="w-full h-28 p-3 bg-transparent border-0 resize-none focus:outline-none"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    onBlur={handleDescriptionBlur}
                    onClick={(e) => e.stopPropagation()}
                    placeholder={t('description-placeholder')}
                    disabled={!isEditUser || isUpdating}
                />
            </div>

            {/* Edit/Delete buttons */}
            <div className="flex flex-row gap-1 items-start pt-2">
                {isEditUser && (
                    <>
                        <button
                            onClick={(e) => { e.stopPropagation(); onEdit(); }}
                            className="btn-primary p-0.5 w-5 h-5"
                            title={t('edit')}
                        >
                            <IconEdit />
                        </button>
                        <DeleteButton
                            deleteAction={onDelete}
                            className="btn-warn p-0.5 w-5 h-5"
                        />
                    </>
                )}
            </div>
        </div>
    )
}

function EditFigureModal({
    figure,
    onClose,
    onSave,
    onDelete
}: {
    figure: DesignFigure
    onClose: () => void
    onSave: (figure: DesignFigure) => Promise<void>
    onDelete: (figureId: number) => Promise<void>
}) {
    const { t } = useTranslation()
    const { imagesLookup } = useBackend()
    const [description, setDescription] = useState(figure.description || '')
    const [isDeleting, setIsDeleting] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    // Get the linked image's imageId from the image-link system
    const linkedImage = imagesLookup[design_figure]?.[figure.figureId]

    const handleSave = async () => {
        setIsSaving(true)
        await onSave({
            ...figure,
            description: description || undefined,
            imageId: linkedImage?.imageId,
        })
        setIsSaving(false)
    }

    const handleDelete = async () => {
        if (figure.figureId) {
            setIsDeleting(true)
            await onDelete(figure.figureId)
        }
    }

    return (
        <Modal blurClick={onClose}>
            <div className="p-4 min-w-[400px] max-w-lg">
                <h4 className="mb-4">
                    {t('edit-figure')}
                </h4>

                {/* Image - uses existing Image component */}
                <div className="mb-4">
                    <div className="label mb-1">{t('image')}</div>
                    <div className="border rounded p-2 bg-gray-50">
                        <Image
                            entity={design_figure}
                            entityId={figure.figureId}
                            isEditable={true}
                        />
                    </div>
                </div>

                {/* Description */}
                <label className="block mb-4">
                    <div className="label mb-1">{t('description')}</div>
                    <textarea
                        className="form-textarea w-full"
                        rows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder={t('description-placeholder')}
                    />
                </label>

                {/* Actions */}
                <div className="flex justify-between pt-4 border-t">
                    <button
                        onClick={handleDelete}
                        disabled={isDeleting}
                        className="btn-warn flex items-center gap-1"
                    >
                        {t('delete')}
                    </button>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="btn-secondary" disabled={isSaving}>
                            {t('cancel')}
                        </button>
                        <button onClick={handleSave} className="btn-primary" disabled={isSaving}>
                            {t('save')}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    )
}
